// Dependency-free so scripts/test-download-design.mjs can run it under plain node (repo jest is broken).

export interface DesignPdfLinks {
  s3Link?: string | null;
  s3CmykLink?: string | null;
}

export interface DownloadableDesign extends DesignPdfLinks {
  id: string;
  designName?: string | null;
}

export type PdfLinkPreference = "cmyk" | "rgb";

type OpenWindow = (url: string, target: string, features?: string) => Window | null;

interface PdfResponse {
  ok: boolean;
  status: number;
  blob: () => Promise<Blob>;
}

export interface DownloadDesignDeps {
  ensureDesignPdf: (designId: string) => Promise<DesignPdfLinks | null>;
  openWindow?: OpenWindow;
  fetchImpl?: (url: string) => Promise<PdfResponse>;
  saveBlob?: (blob: Blob, filename: string) => void;
  prefer?: PdfLinkPreference;
  features?: string;
}

export interface DownloadDesignResult {
  url: string;
  generated: boolean;
  links: DesignPdfLinks;
}

export class DesignDownloadError extends Error {
  readonly designId: string;
  // Set when the PDF was generated but could not be delivered, so the caller can still keep the new links.
  readonly links?: DesignPdfLinks;

  constructor(designId: string, message: string, options?: { cause?: unknown; links?: DesignPdfLinks }) {
    super(message);
    this.name = "DesignDownloadError";
    this.designId = designId;
    this.links = options?.links;
    if (options?.cause !== undefined) (this as { cause?: unknown }).cause = options.cause;
  }
}

export const getStoredPdfLink = (links: DesignPdfLinks, prefer: PdfLinkPreference = "cmyk"): string | null => {
  const ordered = prefer === "cmyk" ? [links.s3CmykLink, links.s3Link] : [links.s3Link, links.s3CmykLink];
  return ordered.find((link): link is string => !!link) ?? null;
};

const defaultOpenWindow: OpenWindow = (url, target, features) => window.open(url, target, features);

const defaultFetch = (url: string): Promise<PdfResponse> => fetch(url);

const defaultSaveBlob = (blob: Blob, filename: string): void => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

const toPdfFilename = (design: DownloadableDesign): string =>
  `${design.designName?.trim() || `design-${design.id}`}.pdf`;

const generatePdfLink = async (
  design: DownloadableDesign,
  ensureDesignPdf: DownloadDesignDeps["ensureDesignPdf"],
  prefer: PdfLinkPreference
): Promise<{ url: string; links: DesignPdfLinks }> => {
  let links: DesignPdfLinks | null;
  try {
    links = await ensureDesignPdf(design.id);
  } catch (error) {
    throw new DesignDownloadError(design.id, `Could not prepare the PDF for design ${design.id}`, { cause: error });
  }
  const url = links ? getStoredPdfLink(links, prefer) : null;
  if (!links || !url) throw new DesignDownloadError(design.id, `No PDF was generated for design ${design.id}`);
  return { url, links };
};

// Figma designs have no stored PDF (PAD-32394): one is generated on demand and saved as a file, not opened in a tab.
export const downloadDesign = async (
  design: DownloadableDesign,
  deps: DownloadDesignDeps
): Promise<DownloadDesignResult> => {
  const {
    ensureDesignPdf,
    openWindow = defaultOpenWindow,
    fetchImpl = defaultFetch,
    saveBlob = defaultSaveBlob,
    prefer = "cmyk",
    features,
  } = deps;
  const storedLink = getStoredPdfLink(design, prefer);
  if (storedLink) {
    openWindow(storedLink, "_blank", features);
    return { url: storedLink, generated: false, links: design };
  }

  const { url, links } = await generatePdfLink(design, ensureDesignPdf, prefer);
  let response: PdfResponse;
  try {
    response = await fetchImpl(url);
  } catch (error) {
    // Same fallback as stored links when the bucket cannot be fetched from this origin (CORS); a noopener open returns null.
    console.warn(`Could not fetch the generated PDF for design ${design.id}, opening it instead`, error);
    openWindow(url, "_blank", features);
    return { url, generated: true, links };
  }
  if (!response.ok) {
    throw new DesignDownloadError(design.id, `PDF download for design ${design.id} failed with ${response.status}`, {
      links,
    });
  }
  saveBlob(await response.blob(), toPdfFilename(design));
  return { url, generated: true, links };
};
